// src/components/layout/ChatColumn/actions/DeleteMessage.jsx
// ==============================================
// DeleteMessage.jsx
// Handles message deletion with confirmation dialog
// ==============================================
import React from 'react';
import PropTypes from 'prop-types';
import { HousePortal } from '@/App';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '@/managers/ThemeProvider';
import { useConversation } from '@/managers/ConvoManager';

const DeleteConfirmationModal = ({ onConfirm, onCancel, isDeleting }) => {
    const { isLightTheme } = useTheme();
    const [dontAsk, setDontAsk] = React.useState(false);
    
    const handleConfirm = () => {
        if (dontAsk) {
            localStorage.setItem('dontAskDeleteConfirmation', 'true');
        }
        onConfirm();
    };
    
    return (
        <HousePortal>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-glass">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className={`
                        ${isLightTheme 
                            ? 'bg-surface text-on-surface border border-border-color'
                            : 'bg-surface bg-opacity-80 border border-white/5'
                        } 
                        backdrop-blur-glass rounded-glass p-6 shadow-glass-md max-w-sm w-full
                    `}
                >
                    <h2 className={`
                        text-lg font-semibold mb-4 
                        ${isLightTheme ? 'text-on-surface' : 'text-white'}
                    `}>
                        Confirm Deletion
                    </h2>
                    <p className={`
                        mb-6 
                        ${isLightTheme ? 'text-on-surface/70' : 'text-white/70'}
                    `}>
                        Are you sure you want to delete this message?
                    </p>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={onCancel}
                            disabled={isDeleting}
                            className={`
                                px-4 py-2 rounded-md hover:bg-surface transition-colors
                                ${isLightTheme 
                                    ? 'bg-surface-dark text-on-surface'
                                    : 'bg-surface-dark text-white/70'}
                                ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isDeleting}
                            className={`px-4 py-2 bg-error text-white rounded-md hover:bg-error-dark transition-colors flex items-center gap-2 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                           {isDeleting ? (
                                <>
                                    <span className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white rounded-full"></span>
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </button>
                    </div>
                    <div className="mt-4 flex items-center">
                        <input
                            type="checkbox"
                            id="dont-ask-again"
                            checked={dontAsk}
                            onChange={(e) => setDontAsk(e.target.checked)}
                            className="mr-2 accent-primary"
                        />
                        <label
                            htmlFor="dont-ask-again"
                            className={`
                                text-sm 
                                ${isLightTheme ? 'text-on-surface/70' : 'text-white/70'}
                            `}
                        >
                            Don't ask again
                        </label>
                    </div>
                </motion.div>
            </div>
        </HousePortal>
    );
};

const DeleteMessage = ({ show, onClose, messageId }) => {
    const { deleteMessage } = useConversation();
    const [isDeleting, setIsDeleting] = React.useState(false);
    
    const performDelete = async () => {
        setIsDeleting(true);
        try {
            const result = await deleteMessage(messageId);
            if (result) {
                onClose(); // Close modal on success
            }
            // Optional: handle failure case with a toast notification
        } catch(error) {
            console.error("Failed to delete message:", error);
            // Optional: show error toast
        } finally {
            setIsDeleting(false);
        }
    };
    
    return (
        <AnimatePresence>
            {show && (
                <DeleteConfirmationModal
                    onConfirm={performDelete}
                    onCancel={onClose}
                    isDeleting={isDeleting}
                />
            )}
        </AnimatePresence>
    );
};

DeleteConfirmationModal.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isDeleting: PropTypes.bool,
};

DeleteMessage.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    messageId: PropTypes.string.isRequired,
};

export default DeleteMessage;